var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { getDisabledElementProps, getHrefValue, getLinkProps, isString, mergeProps, shouldBrowserHandleFocus, useClasses, useConsoleLog, useLayoutProps, useProps, } from '@hawkins/helpers';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { useNavigationContext, usePress } from '../hooks';
import { useAppLogoStyles } from './appLogo.styles';
import StudioLogo from './studioLogo';
var displayName = 'AppLogo';
/**
 * `AppLogo` displays an application’s logo and name.
 *
 * To align with the Netflix brand identity, you should include the Netflix Sans font in your application.
 * Hawkins does not supply Netflix Sans.
 *
 * To install Netflix Sans, make sure your `.npmrc` is pointing to the Netflix internal npm registry.
 * Then, run `npm install netflix-sans` and include the CSS and font files from `node_modules`.
 */
export var AppLogo = React.forwardRef(function (props, ref) {
    var _a, _b, _c;
    // Props
    var _d = useProps(displayName, props), _e = _d.alpha, alpha = _e === void 0 ? false : _e, classesProp = _d.classes, className = _d.className, component = _d.component, _f = _d.disabled, disabled = _f === void 0 ? false : _f, envLabel = _d.envLabel, href = _d.href, icon = _d.icon, layout = _d.layout, name = _d.name, onClick = _d.onClick, rootProps = _d.rootProps, _g = _d.studioLogo, studioLogo = _g === void 0 ? false : _g, styleProp = _d.style, to = _d.to, restProps = __rest(_d, ["alpha", "classes", "className", "component", "disabled", "envLabel", "href", "icon", "layout", "name", "onClick", "rootProps", "studioLogo", "style", "to"]);
    var defaultToStudioLogo = !!studioLogo;
    var label = envLabel;
    var hasLabel = Boolean(label);
    var hasName = Boolean(name);
    useConsoleLog(hasLabel && Boolean(props.alpha), '`props.envLabel` will override the appearance of the `props.alpha` prop', { displayName: displayName, docsPath: 'components-app-logo' });
    var hasClickHandler = !!onClick;
    // Allow for a blank `href` string or even an object (for a custom link component).
    // Assume that `props.to` is populated for a custom link component.
    var isAssumedLink = getHrefValue({ href: href, to: to }) !== null;
    var isClickable = hasClickHandler || isAssumedLink;
    var Component = component || (isAssumedLink ? 'a' : hasClickHandler ? 'button' : 'div');
    var isNativeButton = Component === 'button';
    var pressProps = usePress({ alwaysHandleClick: isAssumedLink && disabled, disabled: disabled, onPress: onClick }).pressProps;
    // Context
    var _h = useNavigationContext(), isCollapsedArea = _h.isCollapsedArea, isTransparent = _h.isTransparent, orientation = _h.orientation;
    // Styles
    var baseClasses = useAppLogoStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var appNameClassName = clsx(classes.appName, (_a = {},
        _a[classes.appNameWithEnvLabel] = hasLabel,
        _a));
    var iconContainerClassName = clsx(classes.iconContainer, (_b = {},
        _b[classes.iconContainerStudioVertical] = orientation === 'vertical' && defaultToStudioLogo && !icon,
        _b));
    var rootClassName = clsx(classes.HAWKINS_BASE, HawkinsGlobalClass.FOCUSABLE, className, classes.root, (_c = {},
        _c[classes.rootClickable] = isClickable,
        _c[classes.rootClickableDisabled] = isClickable && disabled,
        _c[classes.rootTransparent] = isTransparent,
        _c));
    // Content
    var iconNode = icon !== null && icon !== void 0 ? icon : (defaultToStudioLogo && _jsx(StudioLogo, { alpha: alpha || hasLabel, transparent: isTransparent }));
    return (_jsxs(Component, __assign({ className: rootClassName, ref: ref, role: hasClickHandler && !isNativeButton && !isAssumedLink ? 'button' : undefined, style: style, tabIndex: !shouldBrowserHandleFocus(Component) &&
            // The caller might be using a custom link component that resolves to an `<a />` element (e.g., React Router `Link`).
            !(isAssumedLink && !isString(Component)) &&
            hasClickHandler &&
            !disabled
            ? 0
            : undefined, type: isNativeButton ? 'button' : undefined }, getDisabledElementProps(Component, disabled), getLinkProps(Component, { href: href, to: to }), mergeProps(__assign(__assign({}, restProps), rootProps), pressProps), { children: [iconNode && _jsx("div", __assign({ className: iconContainerClassName }, { children: iconNode })), !isCollapsedArea && (hasName || hasLabel) && (_jsxs("div", __assign({ className: classes.textContainer }, { children: [hasName && _jsx("div", __assign({ className: appNameClassName }, { children: name })), hasLabel && _jsx("div", __assign({ className: classes.envLabelContainer }, { children: label }))] })))] })));
});
AppLogo.displayName = displayName;
